/**
 * The forge builder's arithmetic and the one rule for what a box still needs, the same on the
 * storefront and in the dashboard's theme preview: pull rates in percent to weights, the live
 * price, what the creator earns per open, and the first requirement the builder has not met. The
 * price is `forgeBoxPriceCents`, the server's own formula (`forge.ts`), so the price a shopper sees
 * is the price the box is published at.
 */
import {
  FORGE_MIN_ITEMS,
  forgeBoxPriceCents,
  forgeMarginPercent,
  forgeNameIssue,
} from "./forge.js";

/** The weights of a drop table add up to this: parts per million. */
export const FORGE_TOTAL_WEIGHT = 1_000_000;

/** One weight point is this many percent: 100% / 1,000,000. */
const WEIGHT_PER_PERCENT = FORGE_TOTAL_WEIGHT / 100;

/**
 * What the pull-rate field accepts as the shopper types: 0–99 or 100, then up to three decimals
 * (boxed's builder). "" is accepted too, so the field can be cleared.
 */
const PULL_RATE_INPUT = /^(?:100|[0-9]{1,2})(?:\.\d{0,3})?$/;

export function isPullRateInput(text: string): boolean {
  return text === "" || PULL_RATE_INPUT.test(text);
}

/** "12.345" → 123,450. Three decimals of a percent are whole weight points, so this is exact. */
export function percentToWeight(percent: string): number {
  if (percent === "") return 0;
  const value = Number(percent);
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.round(value * WEIGHT_PER_PERCENT);
}

/** 123,450 → "12.345", the form the pull-rate field shows a stored weight in. */
export function weightToPercent(weight: number): string {
  return (weight / WEIGHT_PER_PERCENT).toFixed(3);
}

/** The creator's cut in percent (two decimals at most) → basis points, what the price reads. */
export function cutPercentToBps(percent: number): number {
  return Math.round(percent * 100);
}

/** One row of the builder: a catalog item the shopper picked, and its pull rate as typed. */
export interface ForgeRow {
  id: string;
  name: string;
  imageUrl: string;
  valueCents: number;
  /** The pull-rate field's text, in percent. */
  percent: string;
}

/** Σ weight of the rows, in integer weight points: 100% exactly is `FORGE_TOTAL_WEIGHT`. */
export function totalWeight(rows: readonly ForgeRow[]): number {
  return rows.reduce((sum, row) => sum + percentToWeight(row.percent), 0);
}

/**
 * What one row adds to the price, the "+price" beside its pull rate: its value times its chance,
 * over the win rate (boxed's `calculateIndividualAssetContributionPrice`), rounded up.
 */
export function contributionCents(row: ForgeRow, cutBps: number): number {
  const chance = percentToWeight(row.percent) / FORGE_TOTAL_WEIGHT;
  const winRate = 1 - forgeMarginPercent(cutBps) / 100;
  return Math.ceil((row.valueCents * chance) / winRate);
}

/**
 * The price the builder shows. At exactly 100% it is the server's formula; before that it is
 * the sum of the rows' contributions, so the number grows as the rates are typed and the chips
 * add up to it. (`forgeBoxPriceCents` normalizes by the weights' own sum, so at 60% it would
 * already show a full table's price.)
 */
export function livePriceCents(rows: readonly ForgeRow[], cutBps: number): number {
  if (totalWeight(rows) !== FORGE_TOTAL_WEIGHT) {
    return rows.reduce((sum, row) => sum + contributionCents(row, cutBps), 0);
  }
  return forgeBoxPriceCents(
    rows.map((row) => ({
      id: row.id,
      weight: percentToWeight(row.percent),
      value: row.valueCents,
    })),
    cutBps,
  );
}

/** What the creator earns per open at this price and cut, floored to cents. */
export function earningsPerOpenCents(priceCents: number, cutPercent: number): number {
  return Math.floor((priceCents * cutPercent) / 100);
}

/** Orders the rows by value, cheapest first or dearest first. */
export function sortRowsByValue(rows: readonly ForgeRow[], direction: "asc" | "desc"): ForgeRow[] {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => sign * (a.valueCents - b.valueCents));
}

/**
 * What the builder can still need before it can be submitted, in the order the page reads top to
 * bottom: a name, a name the rules take, an image, enough items, a rate on each, and rates that
 * total 100%.
 */
export const FORGE_SUBMIT_BLOCKERS = [
  "name_empty",
  "name_invalid",
  "image",
  "items",
  "rates",
  "total",
] as const;
export type ForgeSubmitBlocker = (typeof FORGE_SUBMIT_BLOCKERS)[number];

/** What the submit button names: the first thing the box still needs, else the action. */
export type ForgeSubmitKey = ForgeSubmitBlocker | "create" | "save";

export interface ForgeBuilderState {
  mode: "create" | "edit";
  name: string;
  imageUrl: string;
  /** The store's preset images; none offered means no image is needed. */
  imagesOffered: number;
  rows: readonly ForgeRow[];
}

/**
 * The first requirement the builder has not met, in the order the page reads top to bottom, or
 * null when it can be submitted. Editing locks the name and image, so it starts at the items.
 */
export function forgeSubmitBlocker(state: ForgeBuilderState): ForgeSubmitBlocker | null {
  if (state.mode === "create") {
    const issue = forgeNameIssue(state.name);
    if (issue === "empty") return "name_empty";
    if (issue) return "name_invalid";
    if (state.imagesOffered > 0 && !state.imageUrl) return "image";
  }
  if (state.rows.length < FORGE_MIN_ITEMS) return "items";
  if (state.rows.some((row) => percentToWeight(row.percent) <= 0)) return "rates";
  if (totalWeight(state.rows) !== FORGE_TOTAL_WEIGHT) return "total";
  return null;
}

/** The submit button's key: the first unmet requirement, else the action, creating or saving. */
export function forgeSubmitKey(state: ForgeBuilderState): ForgeSubmitKey {
  return forgeSubmitBlocker(state) ?? (state.mode === "edit" ? "save" : "create");
}
